import React, { useState } from 'react';
import type { PatientRecord } from '../../../patients/types';
import { Button } from '../../Button/Button';
import { Icon } from '../../Icon/Icon';
import { Modal } from '../../Modal/Modal';

export interface PatientMessageModalProps {
  patient: PatientRecord;
  open: boolean;
  onClose: () => void;
  onSend?: (text: string) => void;
}

const MAX = 480;

/**
 * Send an SMS to the patient.
 *
 * 🔴 Opt-out is enforced here, not just displayed: a patient who has opted out
 * of SMS cannot be messaged from this dialog at all. That is a compliance
 * boundary, so it blocks rather than warns — the alternative is a UI that lets
 * staff do the thing the law says they can't, and trusts them to read a banner.
 */
export function PatientMessageModal({
  patient,
  open,
  onClose,
  onSend,
}: PatientMessageModalProps) {
  const [text, setText] = useState('');
  const optedOut = patient.contact?.smsOptedOut ?? false;
  const noPhone = !patient.phone;
  const blocked = optedOut || noPhone;
  const canSend = !blocked && text.trim().length > 0;

  const send = () => {
    if (!canSend) return;
    onSend?.(text.trim());
    setText('');
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={`Message ${patient.first}`}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-[12.5px] text-[var(--ink-3)]">
          <Icon kind="phone" size={13} stroke={1.8} className="text-[var(--ink-4)]" />
          <span className="font-[var(--font-mono)]">{patient.phone || 'No phone on file'}</span>
          <span className="ml-auto rounded-[var(--r-pill)] bg-[var(--cream-0)] px-2 py-0.5 text-[11px]">
            SMS
          </span>
        </div>

        {blocked ? (
          <div className="rounded-[var(--r-md)] bg-[var(--terracotta-tint)] px-3 py-2.5 text-[12.5px] text-[var(--terracotta)]">
            {optedOut
              ? `${patient.first} has opted out of SMS. Reach them by phone or email instead.`
              : 'No mobile number on file for this patient.'}
          </div>
        ) : (
          <>
            <textarea
              value={text}
              onChange={(e) => {
                setText(e.target.value.slice(0, MAX));
              }}
              rows={5}
              autoFocus
              placeholder={`Hi ${patient.first}, …`}
              className="w-full resize-none rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3 py-2.5 text-[13.5px] leading-[1.5] text-[var(--ink-1)] outline-none focus:border-[var(--ai-4)]"
            />
            <div className="flex items-center text-[11.5px] text-[var(--ink-4)]">
              <span>Sends as a text message from the practice number</span>
              <span className="ml-auto font-[var(--font-mono)]">
                {text.length}/{MAX}
              </span>
            </div>
          </>
        )}

        <div className="mt-1 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" iconLeft="send" onClick={send} disabled={!canSend}>
            Send
          </Button>
        </div>
      </div>
    </Modal>
  );
}
